
// --------------- Swagger Interfaces ---------------------
export interface AddWalletRequest {
  address: string;
  currency: string;
  name: string;
  network: string;
}

export interface AddressDto {
  address: string;
  created_at: number;
  id: number;
  isStatic: boolean;
  memo: string;
  name: string;
  network: string;
  type: string;
  updated_at: number;
  user_id: number;
}

export interface AddressIdentification {
  name: string;
  category: string;
  description: string;
}

export interface Cluster {
  name: string;
  category: string;
}

export interface CreateReferralLinkRequest {
  name: string;
  targetWallet: string;
}

export interface Exposure {
  category: string;
  value: number;
}

export interface Operation {
  address: string;
  amount: number;
  createdAt: string;
  currency: string;
  description: string;
  fee: number;
  network: string;
  normalizedAmount: number;
  normalizedCurrency: string;
  referenceId: string;
  remainingAmount: number;
  status: "COMPLETED" | "DECLINED" | "PENDING";
  transactionHash: string;
  type: string;
  updatedAt: string;
  user: AccountDto;
  uuid: string;
}

export interface PlanSuggestion {
  amountOfPayedFee: number;
  amountOfPayedFeeUsdt: number;
  customerValue: 'AVERAGE' | 'HIGH' | 'LOW';
  inCount: number;
  outCount: number;
  planResult: PlanResult;
  transfersSpread: { [key: string]: number };
  transfersSpreadDays: { [key: string]: number };
  usesRentals: boolean;
}

export interface PlanResult {
  economy: number;
  minInCount: number;
  minOutCount: number;
  planIntCount: number;
  planMarketFee: number;
  planMarketFeeUsdt: number;
  planOutCount: number;
  planPrice: number;
}

export interface Referral {
  expirationDate: string;
  id: number;
  link: string;
  name: string;
  targetWallet: string;
  type: 'UNIVERSAL' | 'WITH_WALLET';
  user: AccountDto;
}

export interface RegisterUserForm {
  fullName: string;
  locale: string;
  promoCode?: string;
  referralLink?: string;
  telegramChatId: string;
  telegramUsername: string;
}

export interface RegisteredAddressRiskResponse {
  address: string;
  addressIdentifications: AddressIdentification[];
  cluster: Cluster;
  exposures: Exposure[];
  risk: 'HIGH' | 'LOW' | 'MEDIUM' | 'SEVERE';
  riskReason: string;
  status: string;
  triggers: Trigger[];
}

export interface RentResourcesRequest {
  address: string;
  incomingPlan: number;
  outcomingPlan: number;
  days?: number;
}

export interface Rental {
  active: boolean;
  amlPrimeCost: number;
  averageTxFee: number;
  cost: number;
  createdAt: string;
  depositAddressExpireAt: string;
  estimatedMarketCost: number;
  estimatedPrimeCost: number;
  expireAt: string;
  incomingLeft: number;
  incomingPlan: number;
  minAmountOfEnergy: number;
  outcomingLeft: number;
  outcomingPlan: number;
  paymentDate: string;
  periodDays: number;
  planUuid: string;
  receivedAmount: number;
  rentalPrimeCost: number;
  reportPrimeCost: number;
  spentAmount: number;
  startDate: string;
  status: 'ACTIVE' | 'AWAITING_PAYMENT' | 'EXPIRED' | 'EXPIRED_BY_TIME' | 'INVOICE_CANCELED';
  type: 'BONUS' | 'CUSTOM_TRIAL' | 'PAID' | 'TRIAL' | 'TRIAL_EXPIRED';
  updatedAt: string;
  user: AccountDto;
  uuid: string;
  walletAddress: string;
  walletCoin: string;
  walletName: string;
  walletNetwork: string;
  walletRiskLevel: string;
  walletUuid: string;
  wallet: WalletEntity;
  withoutSubscriptionCost: number;
}

export interface RentalDto {
  active: boolean;
  amlPrimeCost: number;
  averageTxFee: number;
  cost: number;
  createdAt: string;
  depositAddress: string;
  depositAddressExpireAt: string;
  estimatedMarketCost: number;
  estimatedPrimeCost: number;
  expireAt: string;
  firstRental: boolean;
  incomingLeft: number;
  incomingPlan: number;
  invoiceId: string | null;
  minAmountOfEnergy: number;
  outcomingLeft: number;
  outcomingPlan: number;
  periodDays: number;
  receivedAmount: number;
  rentalPrimeCost: number;
  reportPrimeCost: number;
  spentAmount: number;
  startDate: string;
  status: 'ACTIVE' | 'AWAITING_PAYMENT' | 'EXPIRED' | 'EXPIRED_BY_TIME' | 'INVOICE_CANCELED';
  type: 'BONUS' | 'CUSTOM_TRIAL' | 'PAID' | 'TRIAL' | 'TRIAL_EXPIRED';
  updatedAt: string;
  userBalanceOnPayment: number;
  uuid: string;
  walletAddress: string;
  walletCoin: string;
  walletName: string;
  walletNetwork: string;
  walletRiskLevel: string;
  walletUuid: string;
  withoutSubscriptionCost: number;
}

export interface Report<T = string> {
  address: string;
  approvedBy: string;
  approvedDate: string;
  commissionAmount: number;
  companyId: string;
  companyName: string;
  createdDate: string;
  data: T;
  id: number;
  isSmartContract: boolean;
  network: string;
  process: ReportProcess;
  providerReports: ProviderReport[];
  riskLevel: 'HIGH' | 'LOW' | 'MEDIUM' | 'SEVERE';
  riskSettingsType: string | 'ADVANCED';
  sentDate: string;
  status: 'COMPLETE' | 'ERROR' | 'PENDING' | 'SENT' | 'VERIFICATION';
  telegramUsername: string;
  token: string;
  type: 'MANUAL' | 'SCHEDULED' | 'TRIAL',
  userUuid: string;
  viewed: boolean;
  walletName: string;
}

export interface ReportWalletRequest {
  wallet: string;
}

export interface RuleTriggered {
  maxThreshold: number;
  minThreshold: number;
  risk: 'HIGH' | 'LOW' | 'MEDIUM' | 'SEVERE';
}

export interface SubmitFraudTransactionRequest {
  coin: string;
  network: string;
  transaction_hash: string;
}

export interface SubmitFraudWalletRequest {
  address: string;
  coin: string;
  network: string;
}

export interface TransactionDto {
  address: AddressDto;
  amount: string;
  created_at: number;
  currency: string;
  fee_amount: string;
  fee_currency: string;
  fromAddress: string;
  hash: string;
  id: string;
  network: string;
  status: string;
  toAddress: string;
  type: string;
  updated_at: number;
}

export interface TransactionOutput {
  address: string;
  amount: number;
}

export interface TransactionInput {
  address: string;
  amount: number;
}

export interface AmlScanEntity<T = string> {
  accidentDate: string;
  account: AccountDto;
  addressFrom: string;
  addressTo: string;
  amount: number;
  amountInUsdt: number | null;
  blockExpireAt: string;
  blockType: 'Permanent' | 'Temporary';
  blockedAmount: number;
  blockedAt: string;
  blockedBy: AccountDto | null;
  blockedTransactionUuid: string;
  caseId: number | null;
  category: string;
  chainanalysisId: string;
  clusterName: string;
  coin: string;
  commissionAmount: number;
  exposures: string;
  feeAmount: number;
  id: number;
  incidentProgressMap: any;
  incidentProgressStep: 'LER_SENT' | 'CASE_CLOSED' | 'CASE_REPORTED' | 'PERMANENT_FREEZE' | 'RECOVERY_OF_FUNDS' | 'TEMPORARY_FREEZE';
  invoiceId?: string;
  isBlockExpired: boolean;
  // hash: string;
  kmsSignatureId: string;
  lastBlockedAt: string;
  latestAmlReport: T | null;
  leaCountry: string;
  network: string;
  nextIncidentProgressStep: 'LER_SENT' | 'CASE_CLOSED' | 'CASE_REPORTED' | 'PERMANENT_FREEZE' | 'RECOVERY_OF_FUNDS' | 'TEMPORARY_FREEZE';
  nextScan: string;
  planName: string;
  providerReports: ProviderReport[];
  riskCategory: string;
  riskScore: 'HIGH' | 'LOW' | 'MEDIUM' | 'SEVERE';
  riskyAmount: number;
  scanCount: number;
  sourceType: 'AMLEnergy' | 'AML_NODE' | 'API' | 'BOT' | 'CLUSTER' | 'MONITOR';
  status: 'COMPLETED' | 'IN_PROCESS' | 'SUBMITTED';
  target: string;
  timeCreated: string;
  timeUpdated: string;
  transactionStatus: 'BROADCAST' | 'CANCELLED' | 'CONFIRMED' | 'CREATED' | 'FAILED';
  transactionTimestamp: string;
  transactionInputs: TransactionInput[];
  transactionOutputs: TransactionOutput[];
  type: 'INCOMING' | 'OUTGOING' | 'TRANSACTION_SCAN' | 'WALLET_SCAN' | 'BLOCKING';
  // user: AccountDto;
  uuid: string;
  victimCountry: string;
  viewed: boolean;
  walletBalance: number;
  walletId: string;
  walletName: string;
  walletType: 'COLD' | 'EXCHANGE' | 'OTHER';
  webhookUrl: string;
}

export interface AccountDto {
  accountType: AccountType;
  accountTypeSource: 'TELEGRAM' | 'WEB';
  apiKeyIssuedAt: string;
  balance: number;
  blocked: boolean;
  cluster: string | null;
  companyDto: CompanyResponse | null;
  companyRole: CompanyRole | null;
  countOfAmlEnergyWallets: number;
  countOfWallets: number;
  country: string;
  crmChatUrl: string | null;
  depositAddress: string;
  domain: string | null;
  estimatedRemainingMonitoringDays: number | null;
  estimatedRemainingReports: number | null;
  feePercent: number;
  hasWallets: boolean;
  heldBalance: number;
  isRevoked: boolean;
  isTelegramInvitationSent: boolean;
  isTest: boolean;
  isWalletCheckEnabled: boolean;
  lastExpiredUserPlan: UserPlan | null;
  leaName: string;
  name: string;
  notes: string;
  paidFeatureLocked: boolean;
  pendingPlanUpgradeInvoice: InvoiceDto | null;
  plan: Plan | null;
  position: string;
  realOutCommissionTrx: number;
  referralCreatePermission: boolean;
  referrer: string;
  role: Role;
  timeCreated: string;
  timeUpdated: string;
  trialExpirationDate: string;
  usedPromoCode: string;
  user: User;
  userPlan: UserPlan;
  uuid: string;
  webhookUrl: string | null;
}

export type Role = 'COMPLIANCE' | 'GENERAL_MANAGER' | 'MANAGER' | 'SUPER_USER' | 'USER' | 'WALLET_STOP_MANAGER' | 'POLICE';

export interface TransactionFraud {
  coin: string;
  hash: string;
  network: string;
  receiver_address: string;
  report: string;
  sender_address: string;
  uuid: string;
}

export interface Trigger {
  category: string;
  percentage: number;
  message: string;
  ruleTriggered: RuleTriggered;
}

export enum CompanyRole {
  AML_OFFICER = 'AML_OFFICER',
  COMPLIANCE_OFFICER = 'COMPLIANCE_OFFICER',
  HEAD_OF_COMPLIANCE = 'HEAD_OF_COMPLIANCE',
  VIEWER = 'VIEWER',
  OWNER = 'OWNER',
  TELEGRAM_VIEWER = 'TELEGRAM_VIEWER'
}

export interface CompanyResponse {
  balance: number;
  lastExpiredUserPlan: UserPlan | null;
  cluster: string | null;
  createdAt: string;
  id: string;
  isRecoverPartner: boolean;
  name: string;
  paidFeatureLocked: boolean;
  pendingPlanUpgradeInvoice: InvoiceDto | null;
  telegramChatId: string | null;
}

export enum AccountType {
  PERSONAL = 'PERSONAL',
  CORPORATE = 'CORPORATE'
}

export type LoginMethod =
  | 'EMAIL_CODE'
  | 'TELEGRAM_WIDGET'
  | 'TELEGRAM_MINI_APP'
  | 'INVITE_ACCEPT'
  | 'COMPANY_REGISTRATION'
  | 'ADMIN_KEY'
  | 'UNKNOWN';

export interface LoginHistoryEntry {
  id: number;
  loginMethod: LoginMethod | string;
  userUuid: string;
  userEmail: string | null;
  userName: string | null;
  accountUuid: string;
  accountType: AccountType | null;
  companyId: string | null;
  companyName: string | null;
  ip: string | null;
  userAgent: string | null;
  device: string | null;
  os: string | null;
  browser: string | null;
  countryCode: string | null;
  country: string | null;
  createdAt: string;
}

export interface LoginHistoryQueryParams {
  accountUuid?: string;
  userUuid?: string;
  loginMethod?: LoginMethod | string;
  from?: string;
  to?: string;
  page?: number;
  size?: number;
  sort?: 'createdAt,asc' | 'createdAt,desc';
}

export type SessionType = 'WEB' | 'TELEGRAM_MINI_APP';

export interface SessionAccount {
  accountUuid: string;
  accountType: AccountType | null;
  companyId: string | null;
  companyName: string | null;
  companyRole: CompanyRole | null;
}

export interface ActiveSession {
  id: string;
  type: SessionType;
  loginMethod: LoginMethod | string;
  current: boolean;
  startedAt: string | null;
  expiresAt: string | null;
  ip: string | null;
  userAgent: string | null;
  device: string | null;
  os: string | null;
  browser: string | null;
  countryCode: string | null;
  country: string | null;
  userUuid: string;
  userEmail: string | null;
  userName: string | null;
  accounts: SessionAccount[];
}

export interface TeamMemberSessions {
  userUuid: string;
  userEmail: string | null;
  userName: string | null;
  sessions: ActiveSession[];
}

export interface CompanySessionsQueryParams {
  accountUuid?: string;
  page?: number;
  size?: number;
}

export interface CompanyMemberDto {
  id: string;
  email: string;
  fullName: string;
  companyRole: CompanyRole | null;
  status: 'ACTIVATED' | 'INVITED' | 'DEACTIVATED' | 'BLOCKED';
  lastLoginAt: string | null;
}


export interface User {
  activeTelegramAccountId: string;
  email: string;
  fullName: string;
  isHowItWorksViewed: boolean;
  isWelcomePageViewed: boolean;
  locale: string;
  phone: string;
  personalAccountId: string;
  telegramChatId: string;
  telegramUsername: string;
  timeCreated: string;
  timeUpdated: string;
  uuid: string;
}

export interface UserPlan {
  balanceUsed: number;
  beosinPrice: number | null;
  billingCycle: 'MONTHLY' | 'YEARLY' | 'CUSTOM';
  bitokPrice: number | null;
  complianceHoursGranted: number;
  complianceHoursUsed: number;
  createdAt: string;
  enabledProviders: string[];
  expirationDate: string | null;
  globalLedgerPrice: number | null;
  hasPaidScheduledRenewal: boolean;
  id: number;
  isLegacy: boolean;
  name: string;
  periodStart: string;
  planOperation: 'RENEW' | 'UPGRADE' | 'DOWNGRADE' | null;
  planPrice: number;
  planPriceMonthly: any;
  planPriceYearly: any;
  reportsUsed: number | null;
  secretSaucePrice: number | null;
  status: 'PENDING' | 'ACTIVE' | 'EXPIRED' | 'CANCELLED' | 'CLOSED';
  updatedAt: string;
  totalPrice: number;
  userPlanType: UserPlanType | string;
}

export interface Wallet {
  address: string;
  coin: string;
  incomingTransactionsCount: number;
  outgoingTransactionsCount: number;
  latestAmlReportJson: string;
  latestAmlReports: string;
  monitoringStatus: 'CANCELED' | 'ACTIVE' | 'INACTIVE';
  monitoringSpent: number;
  createdAt: string;
  name: string;
  network: string;
  riskLevel: string;
  updatedAt: string;
  riskSettingsType: string | 'ADVANCED';
  subscriptionStatus: 'ACTIVE' | 'DISABLED' | 'SUSPENDED';
  subscriptions: SubscriptionDto[];
  uuid: string;
  webhookUrl: string;
  timeCreated: string;
  timeUpdated: string;
  walletOperations: string;
  walletType: string | 'AML_ENERGY' | 'USER_PLAN';
}

export interface SubscriptionDto {
  directions: {
    INCOMING: Providers[],
    OUTGOING: Providers[]
  }
  id: number;
  network: string | 'BTC' | 'BTC_TESTNET' | 'ETH' | 'ETH_SEPOLIA' | 'TRON' | 'TRON_NAIL' | 'TRON_SHASTA';
  status: string | 'ACTIVE' | 'DISABLED' | 'SUSPENDED';
  token: string;
  /** Read-only. Distinguishes a provider-kill-switch pause from a manual disable; not sent back on PUT. */
  availabilityPaused: boolean;
}

export interface WalletEntity {
  address: string;
  coin: string;
  deleted: boolean;
  latestAmlReport: string;
  name: string;
  network: string;
  riskLevel: string;
  timeCreated: string;
  timeUpdated: string;
  user: AccountDto;
  uuid: string;
}

export interface WalletFraud {
  address: string;
  coin: string;
  network: string;
  report: string;
  riskLevel: string;
  uuid: string;
}

export interface WalletWithRentalDto {
  isActivatedPackage: boolean;
  latestExpired: RentalDto;
  latestReportId: string | null;
  process: ReportProcess | null;
  rentals: RentalDto[] | null;
  wallet: Wallet;
}

export interface UpdateWallet {
  address: string;
  name: string;
}

// ---------------------------------------------------------------

export interface Alert {
  alertAmount: string;
  alertLevel: ("HIGH" | "LOW" | "MEDIUM" | "SEVERE");
  category: string;
  categoryId: number;
  exposureType: string;
  externalId: string;
  service: string;
}

export interface Plan {
  id: string;
  minEnergy: number;
  name: string;
  priceUsdt: number;
  txCount: number;
  incomingCount: 0,
  outcomingCount: 0
}

export interface PaginationResponse<T = any> {
  content: T[];
  pageable: {
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    offset: number;
    pageNumber: number;
    pageSize: number;
    unpaged: boolean;
    paged: boolean;
  };
  totalElements: number;
  totalPages: number;
  last: boolean;
  size: number;
  number: number;
  numberOfElements: number;
  first: boolean;
  empty: boolean;
}

export interface TopTenData {
  planSuggestion: PlanSuggestion,
  receiverWallets: ReportDataWallets[],
  senderWallets: ReportDataWallets[],
  registeredAddressRiskResponse: FirstReportDataResponse,
  sourceAddress: string,
  topAlertLevel: "HIGH" | "LOW" | "MEDIUM" | "SEVERE"
}

export interface AmlWalletData {
  address: string;
  addressIdentifications: AddressIdentification[];
  cluster?: Cluster;
  exposures: Exposure[];
  risk: string;
  riskReason?: string;
  status: string;
  triggers: Trigger[];
}

export interface ReportDataWallets {
  address: string;
  amount: number;
  risk: string;
  riskReason: string;
  status: string;
  triggers: Trigger[];
  walletProtected: boolean;
}

export type LatestAmlReport = IngoingTransaction | OutgoingTransaction;

export interface OutgoingTransaction {
  address: string;
  addressIdentifications: AddressIdentification[];
  cluster?: Cluster;
  exposures?: Exposure[];
  risk?: string | null;
  riskReason?: string;
  status: string;
  triggers: Trigger[];
}

export interface IngoingTransaction {
  alerts?: Alert[];
}

export interface ProblemWalletData {
  address: string;
  alertLevel: 'HIGH' | 'LOW' | 'MEDIUM' | 'SEVERE';
  amount: number;
  from: string;
  riskReason?: string;
  riskStats: FirstReportDataResponse;
  to: string;
}

export interface FirstReportData {
  planSuggestion: PlanSuggestion,
  registeredAddressRiskResponse: FirstReportDataResponse
}

export interface FirstReportDataResponse {
  address: string;
  addressIdentifications: AddressIdentification[];
  cluster?: Cluster;
  exposures: Exposure[];
  risk: string;
  riskReason?: string;
  status: string;
  triggers: Trigger[];
}

export interface PlanCost {
  bonusTransactions: number;
  commissionExpenses: number;
  commissionExpensesUsdt: number;
  cost: number | string;
  economy: number | string;
  from: string;
  to: string;
}

export type ReportProcess = 'FIRST_REPORT' | 'INCOMING_TRANSACTION' | 'OUTGOING_TRANSACTION' | 'TOP_10' | 'SUSPISIOUS_WALLET' | 'USER_WALLET' | 'AML_WALLET_CHECK' | 'MONITORING_WALLET_CHECK';

export type ReportData = ProblemWalletData | FirstReportData | TopTenData | AmlWalletData;

export interface TriggerScore {
  nameEn: string;
  nameRu: string;
  risk: 'LOW' | 'MEDIUM' | 'HIGH';
}
export interface InvoiceDto {
  accountId: string;
  accountTelegramUsername: string;
  amount: number;
  amountPaid: number;
  companyId: string;
  companyName: string;
  createdAt: Date;
  creatorEmail: string;
  creatorUuid: string;
  currency: string;
  depositAddress: string;
  depositAddressExpireAt: string;
  description: string;
  issueDate: Date;
  paymentDate: Date;
  receivedAmount: number;
  rental: Rental;
  status: 'ACTIVE' | 'PAID' | 'WAITING_FOR_PAYMENT' | 'CANCELED';
  transactionHash: string | null;
  transactions: TransactionHash[];
  type: 'H24' | 'RENTAL' | 'PLAN_UPGRADE' | 'CUSTOM';
  updatedAt: Date;
  userBalanceAfter: number | null;
  userBalanceBefore: number | null;
  userPlan: UserPlan | null;
  userPlanExpenses: UserPlanExpenses[];
  uuid: string;
}

export interface TransactionHash {
  transactionHash: string;
  amount: number;
  createdAt: string;
}

export interface UserPlanExpenses {
  commissionAmount: number;
  count: number;
  expensesType: 'INCOMING' | 'OUTGOING' | 'TRANSACTION_SCAN' | 'WALLET_SCAN';
  sum: number;
}

export interface WalletCheckNetworkDto {
  additionalText: string;
  id: string;
  name: string;
  sort: number;
  tokens: WalletCheckTokenDef[];
}

export interface WalletCheckTokenDef {
  sort: number;
  name: string;
  providers: Providers[];
  smartContractProviders: Providers[];
  token: string;
}

export interface ScanTokenDef {
  providers: Providers[];
  token: string;
}

export interface WalletCheckQueueDto {
  address: string;
  id: number;
  tokens: ScanTokenDef[] | [];
  network: string;
  reportIds: number[];
  status: 'PAID' | 'PENDING';
}

export type Providers = 'SECRET_SAUCE' | 'GLOBAL_LEDGER' | 'BEOSIN' | 'BITOK';

export interface ProviderReport<T = string> {
  createdAt: string;
  data: T;
  exposures: string | null;
  externalId: string | null;
  id: number;
  provider: string;
  riskCategories: string | null;
  riskScore: string;
  riskyAmount: number | null;
  status: 'IN_PROGRESS' | 'COMPLETED' | 'PENDING';
  updatedAt: string;
}

export type ProviderOutgoings = ProviderOutgoingSecretSauce | ProviderOutgoingBeosin | ProviderOutgoingGlobalLedger | ProviderOutgoingBitok;

export interface ProviderOutgoingSecretSauce {
  address: string;
  addressIdentifications: AddressIdentification[];
  cluster?: Cluster;
  exposures?: Exposure[];
  risk?: string | null;
  riskReason?: string;
  status: string;
  triggers: Trigger[];
}

export interface ProviderOutgoingBeosin {
  incomingDetail: ProviderIncomingBeosinRisk[];
  incomingLevel: 'HIGH' | 'LOW' | 'MEDIUM' | 'SEVERE';
  incomingScore: number;
  outgoingDetail: ProviderIncomingBeosinRisk[];
  outgoingLevel: 'HIGH' | 'LOW' | 'MEDIUM' | 'SEVERE';
  outgoingScore: number;
  riskLevel: 'HIGH' | 'LOW' | 'MEDIUM' | 'SEVERE';
  riskTagDetails: ProviderIncomingBeosinTagRisk[];
  riskTagLevel: 'HIGH' | 'LOW' | 'MEDIUM' | 'SEVERE';
  riskTagScore: number;
  score: number;
}

export interface ProviderIncomingBeosinTagRisk {
  riskTagLevel: 'HIGH' | 'LOW' | 'MEDIUM' | 'SEVERE';
  riskTagDetails: string;
}

export interface ProviderOutgoingGlobalLedger {
  depth: number;
  directTxCount: number;
  fineness: number;
  fullAmount: number | null;
  notMeasured: number | null;
  progress: number;
  scored: number | null;
  sources: ProviderIncomingGlobalLedgerSource[];
  totalAmount: number;
  totalAmountCur: number;
  totalFunds: number;
  totalFundsDefault: number;
  totalFundsSensitive: number;
  totalFundsSeries: number;
}

export interface ProviderOutgoingBitok {
  addressExposures?: {
    entity_category: string;
    entity_name: string;
    exposure: {entity_category: string; value_share: number;}[] | null;
  };
  check: ProviderIncomingBitokCheck;
  risks: ProviderIncomingBitokRisk[];
}

export interface ProviderIncomingBeosinRisk {
  name: string;
  riskDetails: ProviderIncomingBeosinRiskDetails[];
}

export interface ProviderIncomingBeosinRiskDetails {
  amount: number;
  rate: number;
  riskName: string;
}

export interface ProviderIncomingGlobalLedgerSource {
  address: string;
  amount: number;
  amountCur: number;
  amountDec: number;
  currency: string;
  depth: number;
  directStamp: number;
  directTx: string;
  directionUp: boolean;
  entity: any | null;
  funds: {
    id: string | null;
    name: string;
    score: number;
  };
  listType: string;
  owner: string;
  share: number;
  token: string;
  tx_hash: string;
}

export interface ProviderIncomingBitokCheck {
  id: string;
  transfer: ProviderIncomingBitokCheckTransfer;
  check_type: string;
  checked_at: string;
  created_at: string;
  risk_level: string;
  check_status: string;
  fiat_currency: string;
}

export interface ProviderIncomingBitokRisk {
  proximity: string;
  risk_type: string;
  risk_level: string;
  value_share: number;
  value_in_fiat: number;
  entity_category: string;
}

export interface ProviderIncomingBitokCheckTransfer {
  amount: number;
  network: string;
  tx_hash: string;
  token_id: string;
  direction: string;
  occurred_at: string;
  token_symbol: string;
  input_address: string;
  value_in_fiat: number;
  output_address: string;
}

export interface ProviderBitokDirectExposureItem {
  value_in_fiat: number;
  entity_name: string;
  entity_category: string;
}

export interface ProviderBitokIndirectExposureItem {
  value_share: number;
  value_in_fiat: number;
  entity_category: string;
}

export interface ProviderIncomingGlobalLedger {
  depth: number;
  directTxCount: number;
  fineness: number;
  fullAmount: number | null;
  notMeasured: number | null;
  progress: number;
  scored: number | null;
  sources: ProviderIncomingGlobalLedgerSource[];
  totalAmount: number;
  totalAmountCur: number;
  totalFunds: number;
  totalFundsDefault: number;
  totalFundsSensitive: number;
  totalFundsSeries: number;
}

export interface ProviderIncomingBeosin {
  riskLevel: 'HIGH' | 'LOW' | 'MEDIUM' | 'SEVERE';
  risks: ProviderIncomingBeosinRisk[];
  score: number;
}

export interface ProviderIncomingSecretSauce {
  alerts: Alert[];
}

export interface ProviderIncomingBitok {
  addressExposures?: any;
  check: ProviderIncomingBitokCheck;
  risks: ProviderIncomingBitokRisk[];
  transferExposures: ProviderIncomingBitokExposure;
}

export interface ProviderIncomingBitokExposure {
  fiat_currency: string;
  direct_interaction: ProviderBitokDirectExposureItem[];
  indirect_interaction: ProviderBitokIndirectExposureItem[];
}

export type StopWalletTransaction = {
  blockExpireAt: string;
  blockType: string;
  blockedAmount: number;
  blockedAt: string;
  blockedBy: AccountDto;
  category: string | null;
  clusterName: string | null;
  coin: Coin | null;
  hash: string | null;
  kmsSignatureId: string | null;
  network: Network | null;
  receiverWallet: string | null;
  reportData: string | null;
  senderWallet: string | null;
  transactionAmount: number | null;
  transactionStatus: string | null;
  transactionTimestamp: number | null;
  transactionType: string | null;
  uuid: string;
  walletBalance: number | null;
  walletType: 'COLD' | 'EXCHANGE' | 'OTHER';
};

export type Coin = 'BTC' | 'ETH' | 'TRX' | 'USDT';

export type Network = 'BTC' | 'BTC_TESTNET' | 'ETH' | 'ETH_SEPOLIA' | 'TRON' | 'TRON_SHASTA';

export interface PaginationQueryParams {
  page: number;
  size: number;
}

export interface AmlScanQueryParams extends PaginationQueryParams {
  address?: string
  stopWalletAddress?: string;
  from: string;
  to: string;
  amount: number;
  isGrouped: boolean;
  riskScore: string;
  blockType?: string;
  incidentProgressStep?: string[];
  searchBy: string;
  nickname?: string;
  amlScanType?: string;
  sourceType?: string;
}

export interface SupportedNetwork {
  additionalText: string;
  id: string | 'BTC' | 'BTC_TESTNET' | 'ETH' | 'ETH_SEPOLIA' | 'TRON' | 'TRON_NAIL' | 'TRON_SHASTA';
  name: string;
  tokens: Token[];
}

export interface WalletCheckRequest {
  address: string;
  network: string;
}

export interface CreateSubscriptionRequest {
  network: string | 'BTC' | 'BTC_TESTNET' | 'ETH' | 'ETH_SEPOLIA' | 'TRON' | 'TRON_NAIL' | 'TRON_SHASTA';
  tokens: TokenSubscription[];
  walletAddress: string;
}

export interface TokenSubscription {
  directions: {
    INCOMING: Providers[],
    OUTGOING: Providers[]
  }
  token: string;
  status: 'ACTIVE'
}
export interface UpdateWallet {
  address: string;
  name: string;
}
export interface Token {
  id: string;
  name: string;
  providers: Providers[];
}

export interface ReportsList {
  addressFrom: string;
  addressTo: string;
  amount: number;
  createdAt: string;
  network: string;
  providerReportsCount: number;
  recordId: string;
  recordType: 'REPORT' | 'AML_SCAN';
  riskScore: string;
  source: 'AMLEnergy' | 'AML_NODE' | 'API' | 'BOT' | 'CLUSTER' | 'MONITOR' | 'TOP_10' | 'FIRST_REPORT' | 'AML_WALLET_CHECK' | 'MONITORING_WALLET_CHECK';
  status: string;
  target: string;
  token: string;
  type: 'INCOMING' | 'OUTGOING' | 'TRANSACTION_SCAN' | 'WALLET_SCAN' | 'MANUAL';
  viewed: boolean;
}

export type ReportsFilterControl = 'all' | 'incoming_transactions' | 'outgoing_transactions' | 'signals' | '1_time_check' | 'aml_energy' | 'api';
export type ReportsTypeReportControl = 'all' | 'wallets' | 'transactions' | 'stolen';

export interface ReportsFiltersState {
  dateRange?: {
    from?: Date;
    to?: Date;
  };
  searchByControl?: string;
  filterControl?: ReportsFilterControl;
  typeReportControl?: ReportsTypeReportControl;
}

export interface UnifiedActivityQueryParams {
  searchBy?: string;
  from?: string;
  to?: string;
  recordType?: string;
  type?: string;
  source?: string;
  page?: number;
  size?: number;
}

export interface ReportsStats {
  stolenFundsCount: number;
  transactionsCount: number;
  walletChecksCount: number;
}

export interface WalletsStats {
  activeCount: number;
  disabledCount: number;
  suspendedCount: number;
}

export interface WalletTransactionStats {
  incomingCount: number;
  incomingVolume: number;
  outgoingCount: number;
  outgoingVolume: number;
}
export interface ProjectSetting {
  supportUsername: string;
  supportBotUsername: string;
  minBalance: number;
  welcomeBonusAmount: number;
  isAmlEnergyActive: boolean;
  monitoringWalletCheckEnabled: boolean;
}

export type AmlProvider = 'SECRET_SAUCE' | 'GLOBAL_LEDGER' | 'BEOSIN' | 'BITOK';

export type UserPlanType = 'BASIC' | 'PRO' | 'CORPORATE' | 'ENTERPRISE' | string;
export interface PlanTemplateDto {
  id: number;
  type: UserPlanType;
  sortOrder: number;
  name: string;
  description: string;
  generalBenefits: string[];
  yearlyBenefits: string[];
  planPriceMonthly: number;
  planPriceYearly: number;
  secretSaucePrice: number | null;
  bitokPrice: number | null;
  beosinPrice: number | null;
  globalLedgerPrice: number | null;
  enabledProviders: AmlProvider[];
  complianceHoursYearly: number;
  createdAt: string;
  updatedAt: string;
}

export interface BannerDto {
  bannerType: 'WELCOME_BACK' | 'PENDING_INVOICE' | 'EXPIRED' | 'EXPIRY_1D' | 'EXPIRY_7D' | 'EXPIRY_14D' | 'UNFINISHED_TOPUP' | 'SURVEY';
  createdAt: string;
  dismissible: boolean;
  id: number;
  level: 'INFO' | string;
  payload: {
    amount?: number;
    billingCycle?: 'MONTHLY' | 'YEARLY' | 'CUSTOM';
    invoiceId?: string;
    periodEnd?: string;
    periodStart?: string;
    planType?: 'PRO' | 'CORPORATE' | 'ENTERPRISE' | 'CUSTOM';
    address?: string;
    min?: number;
    held?: number;
    rest?: number;
    surveyId?: number;
    priority?: number;
  }
  priority: number;
}

export type SurveyQuestionType = 'SINGLE_CHOICE' | 'FREE_TEXT' | 'MULTI_CHOICE' | 'DROP_DOWN';
export type SurveyStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
export type SurveyResponseStatus = 'IN_PROGRESS' | 'COMPLETED';

export interface SurveyOptionDto {
  id: number;
  position: number;
  text: string;
  nextQuestionId: number | null;
  allowsCustomText: boolean;
  iconSvg: string | null;
}

export interface SurveyQuestionDto {
  id: number;
  position: number;
  type: SurveyQuestionType;
  question: string;
  required: boolean;
  nextQuestionId: number | null;
  options?: SurveyOptionDto[];
}

export interface SurveyAnswerDto {
  questionId: number;
  optionIds?: number[];
  textValue?: string | null;
}

export interface SurveyResponseDto {
  id: number;
  status: SurveyResponseStatus;
  rewardAmount: number | null;
  completedAt: string | null;
  answers: SurveyAnswerDto[];
}

export interface SurveyDto {
  id: number;
  title: string;
  description?: string;
  status: SurveyStatus;
  rewardAmount: number;
  priority: number;
  createdAt: string;
  publishedAt: string | null;
  questions: SurveyQuestionDto[];
  targeting: unknown | null;
  response: SurveyResponseDto | null;
}

export interface SurveyCompleteResponseDto {
  rewardAmount: number;
}

export interface BlockingInfo {
  blockExpireAt: string;
  blockedAmount: number;
  createdAt: string;
  expiresIn: number;
  holderId: string;
  holderName: string;
  submittedBy: string;
  submittedByName: string;
}
